import React, { useEffect, useState } from 'react'
import {
  Container,
  TextCaption,
  TextBody,
  Icon,
  Button,
  State,
  RadioGroup,
  BlockingLoader
} from '@pmi/pmi-iqos-ds-react'
import {
  goTo,
  Routes,
  Fragments,
  ShippingTypes,
  htmlTranslation,
  registerForm,
  unregisterForm,
  validateForm,
  analytics,
  Sites,
  SHIPPING_TK_MAP
} from '@pmi/pmi-iqos-shared-react'
import { CheckoutPickup, CheckoutShipping } from '@zyn-common/components'

const CheckoutStep1 = ({
  translations,
  products,
  shippingMethods,
  selectedShippingMethodIndex,
  handleSelectShippingMethod,
  pickupFailed,
  pickupRegions,
  defaultShippingAddress,
  defaultShippingAddressIsValid,
  hasTobacco,
  handleSearchPickupStores,
  pickupStoreList,
  pickupStoreListNumber,
  handleSelectPickupStore,
  selectedPickupStore,
  pickupStoreListNoResult,
  setMainStateProperty,
  showSaveAsProfileAddress,
  toggleSaveAsProfileAddress,
  updateCheckoutDeliveryOptions,
  isDefaultAddressCheckboxDisabled,
  handleChangeNewShippingAddress,
  enableNextStep,
  loading,
  handleZipCodeNotEligibleForSagawa,
  isSagawa,
  line,
  getSavedNewShippingAddress,
  setNewShippingAddress,
  newShippingAddress,
  setDefaultShippingAddress,
  isMonitorPack,
  setZenrinErrors,
  zenrinErrors,
  isOpen
}) => {
  const [toggleNewShippingAddress, setToggleNewShippingAddress] = useState(
    !defaultShippingAddressIsValid
  )
  const [newShippingAddressIsValid, setNewShippingAddressIsValid] =
    useState(false)
  const [zipCodeEligibleForSagawa, setZipCodeEligibleForSagawa] =
    useState(undefined)
  const [validateAddress, setValidateAddress] = useState(null)
  const [formIsValidTVA, setFormIsValidTVA] = useState(false)
  const [analyticsFiredStepOne, setAnalyticsFiredStepOne] = useState(false)
  const [loadingStep1, setLoadingStep1] = useState(false)

  const formId = registerForm()

  useEffect(() => {
    return () => {
      unregisterForm(formId)
    }
  }, [])

  useEffect(() => {
    if (isOpen && !analyticsFiredStepOne) {
      analytics.virtualPageView('checkout details', 'checkout')
      analytics.checkoutView(products, 1, null, null, Sites.zyn)
      setAnalyticsFiredStepOne(true)
    }
  }, [isOpen])

  useEffect(() => {
    const isValid = validateForm(formId, true)
    setNewShippingAddressIsValid(isValid)
    handleChangeNewShippingAddress()
  }, [newShippingAddress])

  const handleEditTVAClick = () => {
    goTo(
      Routes.profile,
      true,
      false,
      ['editable=edit'],
      Fragments.delivery,
      Sites.zyn
    )
  }

  const zipCodeChangeHandler = selectedOption => {
    if (selectedOption && selectedOption.item) {
      const updatedNewShippingAddress = {
        ...newShippingAddress,
        postalCode: selectedOption.item.zip_code,
        city: selectedOption.item.city + ' ' + selectedOption.item.village,
        state: selectedOption.item.state,
        postalCodeHidden: selectedOption.item.zip_code
      }
      setNewShippingAddress(updatedNewShippingAddress)
      const zipCodeEligible = selectedOption.item.sagawa
      if (isSagawa) {
        setZipCodeEligibleForSagawa(zipCodeEligible)
        handleZipCodeNotEligibleForSagawa(zipCodeEligible)
      }
      if (!isSagawa || (isSagawa && zipCodeEligible)) {
        updateCheckoutDeliveryOptions(updatedNewShippingAddress.postalCode)
      }
    }
  }

  const handleChangeInputsNewShippingAddress = e => {
    const name = e.target.name
    const value = e.target.value
    const updatedNewShippingAddress = { ...newShippingAddress, [name]: value }
    setNewShippingAddress(updatedNewShippingAddress)
  }

  const handleChangeSaveAsProfileAddress = e => {
    setMainStateProperty(e.target.name, e.target.checked)
  }

  const handleChangeShippingAddressType = e => {
    const useDefaultAddress = e.target.value === '0'
    setToggleNewShippingAddress(!useDefaultAddress)
    updateCheckoutDeliveryOptions(
      useDefaultAddress
        ? defaultShippingAddress.postalCode
        : newShippingAddress.postalCode
    )
    setMainStateProperty('useDefaultShippingAddress', useDefaultAddress)
    setMainStateProperty('toggleSaveAsProfileAddress', false)
  }

  const handleShippingMethodChange = e => {
    const index = parseInt(e.target.value || '')
    if (selectedShippingMethodIndex !== index) {
      handleSelectShippingMethod && handleSelectShippingMethod(index)
    }
  }

  const handleConfirmAddress = async () => {
    setLoadingStep1(true)
    if (toggleNewShippingAddress) {
      isMonitorPack
        ? getSavedNewShippingAddress(
            newShippingAddress,
            !toggleNewShippingAddress
          )
        : setValidateAddress(true)
    } else {
      enableNextStep(0)
    }
    setLoadingStep1(false)
  }

  const isDeliverySetupCompleted = () => {
    if (isMonitorPack) {
      return toggleNewShippingAddress && !newShippingAddressIsValid
    } else {
      return toggleNewShippingAddress && !formIsValidTVA
    }
  }

  const renderCheckoutShipping = () => {
    return (
      <CheckoutShipping
        formId={formId}
        shippingAddress={defaultShippingAddress}
        showSaveAsProfileAddress={showSaveAsProfileAddress}
        toggleSaveAsProfileAddress={toggleSaveAsProfileAddress}
        isDefaultAddressCheckboxDisabled={isDefaultAddressCheckboxDisabled}
        toggleNewShippingAddress={toggleNewShippingAddress}
        setToggleNewShippingAddress={setToggleNewShippingAddress}
        newShippingAddress={newShippingAddress}
        handleChangeShippingAddressType={handleChangeShippingAddressType}
        handleEditTVAClick={handleEditTVAClick}
        handleChangeSaveAsProfileAddress={handleChangeSaveAsProfileAddress}
        handleChangeInputsNewShippingAddress={
          handleChangeInputsNewShippingAddress
        }
        handleChangeZipCode={zipCodeChangeHandler}
        hasTobacco={hasTobacco}
        isDefaultAddressValid={defaultShippingAddressIsValid}
        translations={translations}
        loading={loading}
        isSagawa={isSagawa}
        zipCodeEligibleForSagawa={zipCodeEligibleForSagawa}
        line={line}
        validateAddress={validateAddress}
        enableNextStep={enableNextStep}
        setFormIsValidTVA={setFormIsValidTVA}
        setValidateAddress={setValidateAddress}
        getSavedNewShippingAddress={getSavedNewShippingAddress}
        setDefaultShippingAddress={setDefaultShippingAddress}
        isMonitorPack={isMonitorPack}
        setNewShippingAddressIsValid={setNewShippingAddressIsValid}
        setZenrinErrors={setZenrinErrors}
        zenrinErrors={zenrinErrors}
        updateCheckoutDeliveryOptions={updateCheckoutDeliveryOptions}
        setLoadingStep1={setLoadingStep1}
      />
    )
  }

  const renderCheckoutPickup = () => {
    return (
      <CheckoutPickup
        translations={translations}
        pickupRegions={pickupRegions}
        defaultZipCode={defaultShippingAddress.postalCode}
        handleSearchPickupStores={handleSearchPickupStores}
        pickupStoreList={pickupStoreList}
        pickupStoreListNumber={pickupStoreListNumber}
        handleSelectPickupStore={handleSelectPickupStore}
        selectedPickupStore={selectedPickupStore}
        pickupStoreListNoResult={pickupStoreListNoResult}
      />
    )
  }

  const selectedShippingMethod = shippingMethods[selectedShippingMethodIndex]

  const shippingMethodsItems = (() => {
    return shippingMethods && shippingMethods.length > 0
      ? shippingMethods
          .filter(el =>
            isSagawa ? el.shippingId === ShippingTypes.YAMATO : el
          )
          .map((el, index) => ({
            value: index,
            label: (
              <Container
                display={Container.displays.flex}
                alignItems={Container.alignItemsValues.center}
                height="10">
                <TextCaption size={TextCaption.sizes.m}>
                  {translations[
                    'tk_shippingType' + SHIPPING_TK_MAP[el.shippingId]
                  ] || el.shipping}
                </TextCaption>
              </Container>
            ),
            content: (
              <Container fullWidth>
                <TextBody
                  size={TextBody.sizes.m}
                  {...htmlTranslation(
                    translations,
                    'tk_shippingDetailsType' +
                      SHIPPING_TK_MAP[selectedShippingMethod.shippingId]
                  )}
                />
                {el.shippingId === ShippingTypes.YAMATO
                  ? renderCheckoutShipping()
                  : renderCheckoutPickup()}
              </Container>
            ),
            selected: index === selectedShippingMethodIndex,
            disabled:
              (el.shippingId === ShippingTypes.FSS_SIS_LOCKER ||
                el.shippingId === ShippingTypes.CSTORE ||
                el.shippingId === ShippingTypes.FSS) &&
              pickupFailed
          }))
      : []
  })()

  return (
    <>
      <BlockingLoader loading={loadingStep1} />
      <Container>
        {shippingMethods.length > 0 ? (
          <Container>
            <Container pb="2">
              <TextBody
                size={TextBody.sizes.m}
                {...htmlTranslation(
                  translations,
                  'tk_checkoutStepSubtitleShippingMethod'
                )}
              />
            </Container>
            <RadioGroup
              name="shippingMethods"
              label=""
              style={RadioGroup.styles.border}
              columns={1}
              items={shippingMethodsItems}
              getItemLabel={item => item.label}
              getItemValue={item => item.value}
              getItemContent={item => item.content}
              isItemSelected={item => item.selected}
              onChangeHandler={handleShippingMethodChange}
              showOnlySelectedContent
              spacing={RadioGroup.spacingValues.large}
              data-testing="Checkout_RadioGroup_ShippingMethods"
            />
          </Container>
        ) : (
          <Container fullWidth px="4">
            <Container pb="8">
              <TextBody
                size={TextBody.sizes.m}
                {...htmlTranslation(
                  translations,
                  'tk_shippingDetailsType' +
                    SHIPPING_TK_MAP[selectedShippingMethod.shippingId]
                )}
              />
            </Container>
            {selectedShippingMethod.shippingId === ShippingTypes.YAMATO
              ? renderCheckoutShipping()
              : renderCheckoutPickup()}
          </Container>
        )}

        <Container pt="4" px={['0', '20']}>
          <Container
            bg={Container.backgrounds.hardWhite}
            display={Container.displays.flex}
            p="8">
            <Container pr="4" pt="2">
              <State state={State.states.warning} size={State.sizes.xs} />
            </Container>
            <Container>{translations.tk_stepOneNoticeBlock}</Container>
          </Container>
        </Container>

        <Container
          fullWidth
          display={Container.displays.flex}
          alignItems={Container.alignItemsValues.center}
          justifyContent={Container.justifyContentValues.center}
          pt="8"
          pb={['2', '0']}>
          <Button
            text={translations.tk_stepOneSaveButton}
            disabled={isDeliverySetupCompleted() || loading || loadingStep1}
            onClickHandler={handleConfirmAddress}
            variant={Button.variants.primary}
            icon={Icon.images.arrowDown}
            iconPosition={Button.iconPositions.left}
            data-testing="Checkout_Button_SaveStepOne"
          />
        </Container>
      </Container>
    </>
  )
}

export default CheckoutStep1
